// src/layout/builders/ERP/ERPWeeklyUpdateTab.jsx
// ERP: project list → view all submitted weekly updates per project
import React, { useEffect, useState, useCallback } from "react";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import {
  CalendarDays, ArrowLeft, ArrowRight, RefreshCw, Building2,
} from "lucide-react";
import WeeklyUpdateSidebarView from "./WeeklyUpdateSidebarView";

const STATUS_FILTERS = [
  { key: "active", label: "Live", dotColor: "bg-emerald-500" },
  { key: "hold", label: "Hold", dotColor: "bg-amber-400" },
  { key: "completed", label: "Completed", dotColor: "bg-blue-500" },
];

const ProjectWeeklyUpdatesView = ({ project, builderId, onBack }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUpdateId, setSelectedUpdateId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstancev1.get(
        `/erp/weekly-updates/project/${project.project_id}?builderId=${builderId}`
      );
      const submitted = (res.data?.data || []).filter((u) => u.status === "submitted");
      setUpdates(submitted);
    } catch {
      setUpdates([]);
    }
    setLoading(false);
  }, [project.project_id, builderId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 bg-white p-3 rounded-lg border shadow-sm">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 className="font-bold text-gray-800">{project.project_name}</h2>
          <p className="text-xs text-gray-500">{updates.length} submitted weekly update{updates.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-200 border-t-indigo-600" />
        </div>
      ) : updates.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed p-10 text-center text-gray-400 text-sm">
          No submitted weekly updates for this project yet.
        </div>
      ) : (
        <WeeklyUpdateSidebarView
          updates={updates}
          selectedUpdateId={selectedUpdateId}
          onSelectUpdate={setSelectedUpdateId}
          accent="indigo"
          emptyMessage="No submitted weekly updates for this project yet."
        />
      )}
    </div>
  );
};

const ERPWeeklyUpdateTab = () => {
  const { user } = useAuthStore();
  const builderId = user?.builderId || user?.builder_id || user?.id;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [selectedProject, setSelectedProject] = useState(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstancev1.get(
        `/erp/weekly-updates/summary?builderId=${builderId}`
      );
      setProjects(res.data?.data || []);
    } catch {
      setProjects([]);
    }
    setLoading(false);
  }, [builderId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  if (selectedProject) {
    return (
      <ProjectWeeklyUpdatesView
        project={selectedProject}
        builderId={builderId}
        onBack={() => setSelectedProject(null)}
      />
    );
  }

  const filtered = projects.filter((p) => (p.status || "").toLowerCase() === statusFilter);
  const countByStatus = (key) =>
    projects.filter((p) => (p.status || "").toLowerCase() === key).length;

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    completed: "bg-blue-100 text-blue-700 border-blue-200",
    hold: "bg-amber-100 text-amber-700 border-amber-200",
    default: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
            <CalendarDays size={18} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-800">Weekly Updates</h2>
            <p className="text-xs text-gray-500 font-medium">
              {projects.length} project{projects.length !== 1 ? "s" : ""} · Click to view weekly reports
            </p>
          </div>
        </div>
        <button
          onClick={loadProjects}
          className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg w-fit flex-wrap">
        {STATUS_FILTERS.map(({ key, label, dotColor }) => {
          const active = statusFilter === key;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                active ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${dotColor}`} />
              {label}
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-white text-gray-400">
                {countByStatus(key)}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-200 border-t-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed p-10 text-center text-gray-400 text-sm">
          No projects in this category.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            const statusKey = (p.status || "").toLowerCase();
            const badgeCls = statusColors[statusKey] || statusColors.default;
            return (
              <button
                key={p.project_id}
                onClick={() => setSelectedProject(p)}
                className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-indigo-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 size={16} className="text-indigo-500 shrink-0" />
                    <span className="font-bold text-sm text-gray-800 truncate">{p.project_name}</span>
                  </div>
                  <ArrowRight size={16} className="text-gray-300 group-hover:text-indigo-500 shrink-0" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${badgeCls}`}>
                    {p.status || "—"}
                  </span>
                  <span className="text-xs text-gray-500 font-medium">
                    {p.update_count || 0} update{(p.update_count || 0) !== 1 ? "s" : ""}
                  </span>
                </div>
                {p.last_submitted_at && (
                  <p className="text-[10px] text-gray-400 mt-2">
                    Last: {new Date(p.last_submitted_at).toLocaleDateString("en-GB")}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ERPWeeklyUpdateTab;
